import React, { useMemo, useState, useEffect } from 'react';
import { Table, Typography, Button, Grid } from 'antd';
import { DownOutlined, UpOutlined, RightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { LocationData, DateRange } from '../../services/DashboardService';
import { BaselineData } from '../../services/baselineService';
import { formatPercent, getDisplayLocationName } from '../../utils/formatters';
import ExpenseService from '../../services/ExpenseService';

const { Text } = Typography;
const { useBreakpoint } = Grid;

// Percentage points above the tier baseline GFS% that put a store on the High Spenders list
export const HIGH_SPENDER_MARGIN = 1.5;

interface HighSpendersTableProps {
  locations: LocationData[];
  baseline: BaselineData | null;
  tierName: string;
  dashboardEndDate?: string;
}

export const HighSpendersTable: React.FC<HighSpendersTableProps> = ({
  locations,
  baseline,
  tierName,
  dashboardEndDate,
}) => {
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [expenseWeekRanges, setExpenseWeekRanges] = useState<DateRange[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const fetchExpenseWeeks = async () => {
      try {
        const ranges = await ExpenseService.getAvailableExpenseWeeks();
        setExpenseWeekRanges(ranges);
      } catch (error) {
        console.error('Failed to fetch expense weeks:', error);
      }
    };
    fetchExpenseWeeks();
  }, []);

  const highSpenders = useMemo(() => {
    if (!baseline) return [];

    const threshold = baseline.gfsPercent + HIGH_SPENDER_MARGIN;

    return locations
      .filter(loc => {
        const eightWeekGfsPercent = loc.eightWeekGfsPercentValue ??
          (loc.eightWeekTotalSales > 0 && loc.eightWeekTotalGfs
            ? (loc.eightWeekTotalGfs / loc.eightWeekTotalSales) * 100
            : null);

        if (eightWeekGfsPercent === null || eightWeekGfsPercent === undefined) {
          return false;
        }

        return eightWeekGfsPercent > threshold;
      })
      .sort((a, b) => {
        const aPercent = a.eightWeekGfsPercentValue ??
          (a.eightWeekTotalSales > 0 && a.eightWeekTotalGfs
            ? (a.eightWeekTotalGfs / a.eightWeekTotalSales) * 100
            : 0);
        const bPercent = b.eightWeekGfsPercentValue ??
          (b.eightWeekTotalSales > 0 && b.eightWeekTotalGfs
            ? (b.eightWeekTotalGfs / b.eightWeekTotalSales) * 100
            : 0);
        return bPercent - aPercent;
      });
  }, [locations, baseline]);

  if (!baseline || highSpenders.length === 0) {
    return null;
  }

  const threshold = baseline.gfsPercent + HIGH_SPENDER_MARGIN;

  const handleViewLocation = (location: string) => {
    let weekDateRange: DateRange | null = null;

    if (dashboardEndDate && expenseWeekRanges.length > 0) {
      weekDateRange = expenseWeekRanges.find(
        range => range.endDate === dashboardEndDate
      ) || null;
    }

    if (weekDateRange) {
      navigate('/expenses', {
        state: {
          locationToOpen: location,
          dateRange: weekDateRange,
        },
      });
    } else {
      console.warn('Could not find expense week date range for dashboard end date:', dashboardEndDate, 'Available ranges:', expenseWeekRanges);
    }
  };

  const columns = [
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      width: 180,
    },
    {
      title: 'Prior Week GFS %',
      key: 'priorWeekGfsPercent',
      width: 140,
      align: 'right' as const,
      render: (_: unknown, record: LocationData) => {
        const eightWeekGfsPercent = record.eightWeekGfsPercentValue ??
          (record.eightWeekTotalSales > 0 && record.eightWeekTotalGfs
            ? (record.eightWeekTotalGfs / record.eightWeekTotalSales) * 100
            : null);

        if (eightWeekGfsPercent === null || eightWeekGfsPercent === undefined) {
          return '--';
        }
        return formatPercent(eightWeekGfsPercent);
      },
    },
    {
      title: 'Threshold',
      key: 'threshold',
      width: 100,
      align: 'right' as const,
      render: () => formatPercent(threshold),
    },
    {
      title: 'Over By',
      key: 'overBy',
      width: 100,
      align: 'right' as const,
      render: (_: unknown, record: LocationData) => {
        const eightWeekGfsPercent = record.eightWeekGfsPercentValue ??
          (record.eightWeekTotalSales > 0 && record.eightWeekTotalGfs
            ? (record.eightWeekTotalGfs / record.eightWeekTotalSales) * 100
            : null);

        if (eightWeekGfsPercent === null || eightWeekGfsPercent === undefined) {
          return '--';
        }
        const over = eightWeekGfsPercent - threshold;
        if (over <= 0) {
          return '--';
        }
        return <Text type="danger">+{over.toFixed(2)}%</Text>;
      },
    },
    {
      title: 'DM',
      dataIndex: 'group',
      key: 'dm',
      width: 150,
    },
    {
      title: '',
      key: 'action',
      width: 80,
      render: (_: unknown, record: LocationData) => (
        <Button
          type="link"
          size="small"
          onClick={() => handleViewLocation(record.location)}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <div
      style={{
        marginBottom: 16,
        border: '1px solid #ffccc7',
        borderLeft: '4px solid #ff4d4f',
        borderRadius: 6,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text strong style={{ fontSize: 14, color: '#cf1322' }}>
            High Spenders
          </Text>
          <span
            style={{
              backgroundColor: '#ff4d4f',
              color: '#fff',
              padding: '2px 8px',
              borderRadius: 10,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {highSpenders.length}
          </span>
          <Text type="secondary" style={{ fontSize: 13 }}>
            above {formatPercent(threshold)} threshold
          </Text>
        </div>
        <Button
          type="text"
          size="small"
          icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
          style={{ color: '#8c8c8c' }}
        />
      </div>
      {isExpanded && (
        <div style={{ padding: '0 16px 12px 16px' }}>
          {isMobile ? (
            // Mobile: Card-based list
            <div>
              {highSpenders.map((record) => {
                const eightWeekGfsPercent = record.eightWeekGfsPercentValue ??
                  (record.eightWeekTotalSales > 0 && record.eightWeekTotalGfs
                    ? (record.eightWeekTotalGfs / record.eightWeekTotalSales) * 100
                    : null);
                const over = eightWeekGfsPercent !== null ? eightWeekGfsPercent - threshold : 0;

                return (
                  <div
                    key={record.location}
                    onClick={() => handleViewLocation(record.location)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px',
                      backgroundColor: '#fff',
                      borderRadius: 4,
                      marginBottom: 8,
                      cursor: 'pointer',
                      border: '1px solid #f0f0f0',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <Text strong style={{ fontSize: 14, display: 'block' }}>
                        {getDisplayLocationName(record.location)}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {record.group}
                      </Text>
                    </div>
                    <div style={{ textAlign: 'right', marginRight: 8 }}>
                      <Text strong style={{ fontSize: 16, display: 'block' }}>
                        {eightWeekGfsPercent !== null ? formatPercent(eightWeekGfsPercent) : '--'}
                      </Text>
                      <Text type="danger" style={{ fontSize: 12 }}>
                        +{over.toFixed(2)}% over
                      </Text>
                    </div>
                    <RightOutlined style={{ color: '#bfbfbf', fontSize: 12 }} />
                  </div>
                );
              })}
            </div>
          ) : (
            // Desktop: Table view
            <Table
              columns={columns}
              dataSource={highSpenders}
              rowKey="location"
              pagination={false}
              size="small"
              scroll={{ x: 'max-content' }}
              style={{
                backgroundColor: '#fff',
                borderRadius: 4,
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};
